import { useState, useEffect, useCallback } from 'react';
import { enhancedApi } from '../../../shared/services/enhancedApi';

export interface LocalAddressProvince {
  provinceCode: string;
  descr: string;
  createdBy: string;
  createdDate: string;
  updatedBy: string | null;
  updatedDate: string | null;
}

interface UseLocalAddressesProvincesReturn {
  provinces: LocalAddressProvince[];
  provinceOptions: string[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useLocalAddressesProvinces = (): UseLocalAddressesProvincesReturn => {
  const [provinces, setProvinces] = useState<LocalAddressProvince[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchLocalAddressesProvinces = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Use enhanced API with caching and queuing
      const response = await enhancedApi.cachedGet<{
        success: boolean;
        provinces: LocalAddressProvince[];
      }>(
        '/api/local-addresses/provinces',
        {},
        30 * 60 * 1000 // 30 minutes TTL - provinces rarely change
      );

      if (response.success && response.provinces) {
        const sortedProvinces = [...response.provinces].sort((a, b) =>
          a.descr.localeCompare(b.descr)
        );

        console.log("Fetched local addresses provinces (cached/sorted):", sortedProvinces);

        setProvinces(sortedProvinces);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('Error fetching local addresses provinces:', err);
      setError('Failed to fetch local addresses provinces');
    } finally {
      setLoading(false);
    }
  }, []); // No dependencies needed since api and setters are stable

  useEffect(() => {
    fetchLocalAddressesProvinces();
  }, [fetchLocalAddressesProvinces]);

  // Convert provinces to options format for dropdowns using description
  const provinceOptions = provinces.map(province => province.descr);

  return {
    provinces,
    provinceOptions,
    loading,
    error,
    refetch: fetchLocalAddressesProvinces
  };
};
